import { Box, LinearProgress, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { DeviceHardware, InstallationCommandResultStatusEnum } from '../../../../../../backendsdk';
import { TrackedButton as Button } from '../../../../../../components/TrackedComponents';
import useApi from '../../../../../../hooks/api';
import { Pages } from '../../SetupTool';
import { setStoredValue } from '../../utils/storage';

const PENDING = InstallationCommandResultStatusEnum.Pending;
const SUCCESS = InstallationCommandResultStatusEnum.Success;
const FAILURE = InstallationCommandResultStatusEnum.Failure;

type CommandResult = typeof PENDING | typeof SUCCESS | typeof FAILURE;
const POLLING_TIME_FACTOR = 1600;
const EXPECTED_POLLING_TIME = 180;

interface ReInstallationVerificationProps {
    installerName: string;
    deviceId: string;
    onComplete: () => void;
    dashboardLink: React.ReactNode;
}

const getCurrentTimestamp = () => {
    return Math.floor(Date.now() / 1000);
};

const ReInstallationVerification: React.FC<ReInstallationVerificationProps> = (props) => {
    const [cleanupCommandStatus, setCleanupCommandStatus] = useState<CommandResult | null>(null);
    const [formatCommandStatus, setFormatCommandStatus] = useState<CommandResult | null>(null);
    const [isPolling, setIsPolling] = useState<boolean>(true);
    const [startPollingTime] = useState<number>(getCurrentTimestamp() - POLLING_TIME_FACTOR);
    const [currentTimestamp, setCurrentTimestamp] = useState<number>(getCurrentTimestamp());
    const { t } = useTranslation();
    const { api } = useApi();
    const history = useHistory();

    const updateStatusFromCommand = useCallback(
        (
            command: { status?: string } | undefined,
            setStatus: React.Dispatch<React.SetStateAction<CommandResult | null>>,
        ) => {
            if (!command) {
                return;
            }

            const status = command.status === SUCCESS ? SUCCESS : command.status === FAILURE ? FAILURE : PENDING;
            setStatus(status);
        },
        [],
    );

    const pollCommands = useCallback(async () => {
        try {
            // Get commands from the last 10 minutes to catch recently created commands
            const timeFrom = startPollingTime;
            const timeTo = getCurrentTimestamp();
            const response = await api.apiV0CommandGet({
                timeFrom,
                timeTo,
                command: 'installation_cleanup,format_sd_card',
            });

            // Filter commands for this device
            const deviceCommands = response.data.filter((cmd) => {
                return cmd.device?.device_id === props.deviceId;
            });

            const cleanupCommand = deviceCommands.find((cmd) => cmd.command === 'installation_cleanup');
            const formatCommand = deviceCommands.find((cmd) => cmd.command === 'format_sd_card');

            updateStatusFromCommand(cleanupCommand, setCleanupCommandStatus);
            updateStatusFromCommand(formatCommand, setFormatCommandStatus);

            // Stop polling if both commands are completed (success or failure)
            if (
                cleanupCommand &&
                formatCommand &&
                cleanupCommand.status !== PENDING &&
                formatCommand.status !== PENDING
            ) {
                setIsPolling(false);
            }
        } catch (error) {
            console.error('Error polling commands:', error);
        }
    }, [api, props.deviceId, updateStatusFromCommand]);

    // Poll for remote commands
    useEffect(() => {
        if (!isPolling) {
            return;
        }

        // Poll every 5 seconds
        const interval = setInterval(pollCommands, 5000);
        pollCommands(); // Initial poll

        return () => clearInterval(interval);
    }, [isPolling, pollCommands]);

    // Update current time periodically for progress calculation
    useEffect(() => {
        const bothCommandsCompleted = cleanupCommandStatus !== null && formatCommandStatus !== null && !isPolling;
        if (bothCommandsCompleted) {
            return;
        }

        // Update every second for smooth progress bar
        const interval = setInterval(() => {
            setCurrentTimestamp(getCurrentTimestamp());
        }, 1000);

        return () => clearInterval(interval);
    }, [cleanupCommandStatus, formatCommandStatus, isPolling]);

    const bothCommandsCompleted = cleanupCommandStatus !== null && formatCommandStatus !== null && !isPolling;
    const bothCommandsSuccess = cleanupCommandStatus === SUCCESS || formatCommandStatus === SUCCESS;
    const completedCount =
        Number(cleanupCommandStatus && cleanupCommandStatus !== PENDING) +
        Number(formatCommandStatus && formatCommandStatus !== PENDING);

    // Calculate time-based progress
    const elapsedTime = currentTimestamp - (startPollingTime + POLLING_TIME_FACTOR);
    const timeProgress = Math.min((elapsedTime / EXPECTED_POLLING_TIME) * 100, 95);

    // Calculate command-based progress
    const commandProgress = completedCount === 2 ? 100 : 25;

    // Max between time and command progress
    const progressValue = Math.round(Math.max(timeProgress, commandProgress));

    const handleVerifyCamera = useCallback(() => {
        const verifySuffix = 'free-verify';
        setStoredValue<string>('OE-installer-name', '', props.installerName);
        setStoredValue<number>('OE-page', verifySuffix, Pages.VerifyCamera);
        setStoredValue('OE-new-device', verifySuffix, { imei: props.deviceId, hardware: DeviceHardware.ReachengR1 });
        history.push('/tasks/verify');
    }, [history, props.deviceId, props.installerName]);

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                <Typography variant="h6" textAlign="center">
                    {t('setup_tool.change_vin.re_installation.header')}
                </Typography>

                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, px: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography fontSize={14} textAlign="center" sx={{ fontWeight: 500 }}>
                            {t('setup_tool.change_vin.re_installation.instructions')}
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 2, width: '100%' }}>
                        <LinearProgress
                            variant="determinate"
                            value={progressValue}
                            color={bothCommandsCompleted && bothCommandsSuccess ? 'success' : 'primary'}
                        />
                        <Typography fontSize={12} textAlign="center" sx={{ color: 'text.secondary', mt: 1 }}>
                            {progressValue}%
                        </Typography>
                    </Box>

                    {bothCommandsCompleted && (
                        <Box sx={{ mt: 2 }}>
                            {bothCommandsSuccess ? (
                                <Typography fontSize={14} color="success.main" textAlign="center" sx={{ mb: 2 }}>
                                    {t('setup_tool.change_vin.re_installation.installation_completed')}
                                </Typography>
                            ) : (
                                <Typography fontSize={14} color="error.main" textAlign="center" sx={{ mb: 2 }}>
                                    {t('setup_tool.change_vin.re_installation.installation_failed')}
                                </Typography>
                            )}
                            <Typography fontSize={14} textAlign="center" sx={{ mb: 2 }}>
                                {t('setup_tool.change_vin.re_installation.suggest_verify_camera')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
            {bothCommandsCompleted ? (
                <Box sx={{ width: '100%', display: 'flex', p: 2, minHeight: 0, flexShrink: 0, gap: 1 }}>
                    <Button
                        id="btn-verify-camera"
                        fullWidth
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            handleVerifyCamera();
                        }}
                    >
                        {t('setup_tool.change_vin.re_installation.verify_camera')}
                    </Button>
                    <Button
                        id="btn-done"
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            props.onComplete();
                        }}
                    >
                        {t('setup_tool.completed.done')}
                    </Button>
                </Box>
            ) : (
                props.dashboardLink
            )}
        </>
    );
};

export default ReInstallationVerification;
